import React, { FC, useCallback, useEffect, useState } from 'react'

import { DeleteOutlined } from '@ant-design/icons'
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  PageHeader,
  Radio,
  Row,
  Table,
} from 'antd'
import { SelectFieldAnt } from 'Components/Form'
import { SelectItemProps } from 'Components/Form/Select'
import {
  Empresa,
  FabricanteRastreador,
  GerenciadorPosicao,
  ModoCobranca,
  Rastreador,
} from 'Models/cadastros'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import WsCadastroService from 'Services/WS_CADASTRO'
import { MsgSuccessOptions } from 'Types/SnackbarTypes'

import ModalEmpresaAssociada from '../Modais/EmpresaAssociada'

interface IProps {
  mode: string
}

const FormRastreadores: FC<IProps> = ({ mode }: IProps) => {
  const navigate = useNavigate()
  const hookForm = useForm()
  const [openSnackbarSuccess, closeSnackbarSuccess] =
    useSnackbar(MsgSuccessOptions)
  const [itemData, setItemData] = useState<Rastreador>()
  const [carregandoInclusaoOuEdicao, setCarregandoInclusaoOuEdicao] =
    useState<boolean>(false)
  const { id } = useParams()
  const [cdFabricante, setCdFabricante] = useState<number>()
  const [fabricantes, setFabricantes] = useState<FabricanteRastreador[]>([])
  const [selectFabricanteItens, setSelectFabricanteItens] = useState<
    SelectItemProps[]
  >([])
  const [selectGerenciadorPosicaoItens, setSelectGerenciadorPosicaoItens] =
    useState<SelectItemProps[]>([])
  const [selectModoCobrancaItens, setSelectModoCobrancaItens] = useState<
    SelectItemProps[]
  >([])
  const [mestreSistema, setMestreSistema] = useState<boolean>(false)
  const [modalEmpresaAssociada, setModalEmpresaAssociada] =
    useState<boolean>(false)
  const [empresasAssociadas, setEmpresasAssociadas] = useState<Empresa[]>([])
  const [formulario] = Form.useForm<Rastreador>()

  const getFabricantes = useCallback(async () => {
    const { data } = await WsCadastroService.rastreadores.getListFabricantes()

    if (data) {
      setFabricantes(data)
    }
  }, [])

  const getGerenciadoresPosicoes = useCallback(async () => {
    const { data } =
      await WsCadastroService.rastreadores.getListGerenciadoresPosicoes()

    if (data) {
      const selectGerenciadorPosicaoData: SelectItemProps[] = data.map(
        (item: GerenciadorPosicao): SelectItemProps => {
          return {
            value: item.id.toString(),
            label: item.nome,
          }
        },
      )

      setSelectGerenciadorPosicaoItens(selectGerenciadorPosicaoData)
    }
  }, [])

  const getModosCobrancas = useCallback(async () => {
    const { data } =
      await WsCadastroService.rastreadores.getListModosCobrancas()

    if (data) {
      const selectModoCobrancaData: SelectItemProps[] = data.map(
        (item: ModoCobranca): SelectItemProps => {
          return {
            value: item.id.toString(),
            label: item.nome,
          }
        },
      )

      setSelectModoCobrancaItens(selectModoCobrancaData)
    }
  }, [])

  const verificaMestreSistema = useCallback(async () => {
    const { data } = await WsCadastroService.usuarios.verificaMestreSistema()

    if (data) {
      setMestreSistema(data)
    }
  }, [])

  useEffect(() => {
    getFabricantes()
    getGerenciadoresPosicoes()
    getModosCobrancas()
    verificaMestreSistema()
  }, [])

  useEffect(() => {
    if (fabricantes) {
      const selectFabricanteData: SelectItemProps[] = fabricantes.map(
        (item: FabricanteRastreador): SelectItemProps => {
          return {
            value: item.id.toString(),
            label: item.nome,
          }
        },
      )

      setSelectFabricanteItens(selectFabricanteData)
    }
  }, [fabricantes])

  const getItemData = useCallback(async (idRastreador) => {
    const { data } = await WsCadastroService.rastreadores.details(idRastreador)
    data.cdFabricante = data.fabricante.id.toString()
    data.cdModoCobranca = data?.objetoRastreavel?.cdModoCobranca?.toString()
    setItemData(data)
  }, [])

  useEffect(() => {
    if (id) {
      getItemData(parseInt(id))
    }
  }, [id, getItemData])

  const cadastraOuEditaRastreador = async (rastreador: Rastreador) => {
    setCarregandoInclusaoOuEdicao(true)

    if (rastreador.ativo === undefined) {
      rastreador.ativo = true
    }

    rastreador.objetoRastreavel = itemData?.objetoRastreavel

    if (rastreador.cdModoCobranca && rastreador.objetoRastreavel) {
      rastreador.objetoRastreavel.cdModoCobranca = parseInt(
        rastreador.cdModoCobranca,
      )
    }

    const fabricante: FabricanteRastreador | undefined = fabricantes.find(
      (f) => f.id == parseInt(rastreador.cdFabricante),
    )

    if (fabricante) {
      rastreador.fabricante = fabricante
    }

    rastreador.entidades = empresasAssociadas

    const { data } =
      mode === 'add'
        ? await WsCadastroService.rastreadores.create(rastreador)
        : await WsCadastroService.rastreadores.update(rastreador, id)

    if (data) {
      if (data.mensagem) {
        openSnackbarSuccess(data.mensagem)
      }

      if (data.statusCode === 200 || data.statusCode === 201) {
        navigate(-1)
      } else {
        setCarregandoInclusaoOuEdicao(false)
      }
    }
  }

  useEffect(() => {
    if (itemData) {
      formulario.setFieldsValue(itemData)
      setEmpresasAssociadas(itemData.entidades)
    }
  }, [itemData])

  const removeEmpresa = (id: number) => {
    setEmpresasAssociadas(empresasAssociadas.filter((f) => f.id !== id))
  }

  return (
    <>
      <Form
        onFinish={cadastraOuEditaRastreador}
        layout="vertical"
        form={formulario}
      >
        <PageHeader
          ghost={false}
          title={mode === 'add' ? 'Incluir Rastreador' : 'Editar Rastreador'}
          extra={[
            <Button
              key="insercaoOuEdicao"
              type="primary"
              htmlType="submit"
              loading={carregandoInclusaoOuEdicao}
            >
              {mode === 'add' ? 'Incluir' : 'Salvar'}
            </Button>,

            <Button key="voltar" type="primary" onClick={() => navigate(-1)}>
              Voltar
            </Button>,
          ]}
        >
          <Row gutter={16}>
            <Col span={6} md={6} sm={12} xs={24}>
              <Form.Item
                name="numero"
                label="Número:"
                rules={[{ required: true, message: 'Campo obrigatório' }]}
              >
                <Input type="text" />
              </Form.Item>
            </Col>

            <Col span={6} md={6} sm={12} xs={24}>
              <Form.Item name="apelido" label="Apelido:">
                <Input type="text" />
              </Form.Item>
            </Col>

            <Col span={6} md={6} sm={12} xs={24}>
              <SelectFieldAnt
                methods={hookForm}
                scope="form-rastreadores"
                label="Tecnologia:"
                name="cdFabricante"
                items={selectFabricanteItens}
                showSearch={true}
                onChangeValue={(cdFabricante: string) =>
                  setCdFabricante(parseInt(cdFabricante))
                }
                rules={[{ required: true, message: 'Campo obrigatório' }]}
              />
            </Col>

            <Col span={6} md={6} sm={12} xs={24}>
              <Form.Item name="ativo" label="Situação:">
                <Radio.Group defaultValue={true}>
                  <Radio value={true}>Ativo</Radio>
                  <Radio value={false}>Inativo</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            {mestreSistema ? (
              <>
                <Col span={9} md={9} sm={12} xs={24}>
                  <SelectFieldAnt
                    methods={hookForm}
                    scope="form-rastreadores"
                    label="Gerenciador de Posição:"
                    name="cdGerenciadorPosicao"
                    items={selectGerenciadorPosicaoItens}
                    showSearch={true}
                  />
                </Col>

                <Col span={9} md={9} sm={12} xs={24}>
                  <SelectFieldAnt
                    methods={hookForm}
                    scope="form-rastreadores"
                    label="Modo de Cobrança:"
                    name="cdModoCobranca"
                    items={selectModoCobrancaItens}
                    showSearch={true}
                    disabled={!itemData?.objetoRastreavel}
                  />

                  {!itemData?.objetoRastreavel ? (
                    <div style={{ color: '#ff4d4f', marginTop: '-22px' }}>
                      Vincule o rastreador a um veículo antes de selecionar um
                      modo de cobrança
                    </div>
                  ) : null}
                </Col>
              </>
            ) : null}

            {cdFabricante === 13 || mestreSistema ? (
              <Col
                span={6}
                md={6}
                sm={12}
                xs={24}
                style={{
                  display:
                    cdFabricante === 13 || mestreSistema ? 'block' : 'none',
                }}
              >
                <Form.Item name="telemetria" label="Telemetria:">
                  <Radio.Group>
                    <Radio value={true}>Sim</Radio>
                    <Radio value={false}>Não</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            ) : null}
          </Row>
        </PageHeader>
      </Form>

      <Divider>Selecionar Empresas para o Rastreador</Divider>

      <Row gutter={16}>
        <Col span={24}>
          <Table
            columns={[
              {
                title: '',
                key: 'id',
                dataIndex: 'id',
                width: '25px',
                render: (id: number) => (
                  <div
                    style={{ cursor: 'pointer' }}
                    title="Remover"
                    onClick={() => removeEmpresa(id)}
                  >
                    <DeleteOutlined />
                  </div>
                ),
              },
              {
                title: 'Empresa',
                key: 'nome',
                dataIndex: 'nome',
              },
              {
                title: 'CNPJ',
                key: 'cnpj',
                dataIndex: 'cnpj',
              },
            ]}
            dataSource={empresasAssociadas || []}
            scroll={{ y: 300 }}
            pagination={false}
          />
        </Col>
      </Row>

      <Button type="primary" onClick={() => setModalEmpresaAssociada(true)}>
        Adicionar
      </Button>

      <Modal
        centered
        visible={modalEmpresaAssociada}
        onCancel={() => setModalEmpresaAssociada(false)}
        footer={[]}
        destroyOnClose={true}
        width={1000}
      >
        <div style={{ height: '600px' }}>
          <ModalEmpresaAssociada
            empresas={empresasAssociadas}
            setEmpresas={setEmpresasAssociadas}
          />
        </div>
      </Modal>
    </>
  )
}

export default FormRastreadores
